import {
    Badge,
    makeStyles,
    Menu,
    MenuCheckedValueChangeData,
    MenuCheckedValueChangeEvent,
    MenuItemCheckbox,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import { ChevronDownRegular, FilterRegular } from '@fluentui/react-icons';

export interface DirectoryFilterOption {
    value: string;
    label: string;
    /** Per-value count, shown when the backend provides facet counts. */
    count?: number;
}

const useStyles = makeStyles({
    trigger: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        height: '36px',
        padding: '0 12px',
        borderRadius: tokens.borderRadiusMedium,
        border: `1px dashed ${tokens.colorNeutralStroke1}`,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground2,
        fontFamily: tokens.fontFamilyBase,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        cursor: 'pointer',
        ':hover': {
            backgroundColor: tokens.colorSubtleBackgroundHover,
            color: tokens.colorNeutralForeground1,
        },
    },
    triggerActive: {
        border: `1px solid ${tokens.colorBrandStroke1}`,
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1,
    },
    icon: {
        fontSize: '16px',
        color: tokens.colorNeutralForeground3,
    },
    iconActive: {
        color: tokens.colorBrandForeground1,
    },
    count: {
        minWidth: '18px',
    },
    optionCount: {
        marginLeft: 'auto',
        paddingLeft: '12px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
    },
});

interface DirectoryFilterProps {
    /** Stable key used as the menu checkbox group name. */
    name: string;
    label: string;
    options: DirectoryFilterOption[];
    selected: string[];
    onChange: (values: string[]) => void;
}

/** Multi-select facet filter: dashed trigger → solid navy + count when active. */
const DirectoryFilter = ({ name, label, options, selected, onChange }: DirectoryFilterProps) => {
    const styles = useStyles();
    const active = selected.length > 0;

    const handleChange = (_e: MenuCheckedValueChangeEvent, data: MenuCheckedValueChangeData) => {
        onChange(data.checkedItems);
    };

    return (
        <Menu checkedValues={{ [name]: selected }} onCheckedValueChange={handleChange}>
            <MenuTrigger disableButtonEnhancement>
                <button
                    type="button"
                    className={mergeClasses(styles.trigger, active && styles.triggerActive)}
                    aria-label={`Filter by ${label}`}
                >
                    <FilterRegular className={mergeClasses(styles.icon, active && styles.iconActive)} />
                    {label}
                    {active && (
                        <Badge className={styles.count} appearance="filled" color="brand" size="small">
                            {selected.length}
                        </Badge>
                    )}
                    <ChevronDownRegular className={styles.icon} />
                </button>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {options.map((opt) => (
                        <MenuItemCheckbox key={opt.value} name={name} value={opt.value}>
                            {opt.label}
                            {opt.count !== undefined && <span className={styles.optionCount}>{opt.count}</span>}
                        </MenuItemCheckbox>
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

export default DirectoryFilter;
